import React from 'react';
import Delayed from 'react-delayed';
import { find, cloneDeep } from 'lodash';
import pt from 'prop-types';

import { isEmpty, classNames, objectToArray, locationWithQuery } from 'utils';
import { translateAppLink } from 'utils/app';
import { openFrontendAccessUrl } from 'utils/app-frontend';
import { expandString } from 'utils/string';
import { openWebUrl } from 'utils/web-url';

import Icon from 'components/icon';
import { Form, Field } from 'components/form';
import Checkbox from 'components/icon/checkbox';
import { FadeLoading, MoveUp } from 'components/transitions';
import Pagination from 'components/collection/pagination';
import CollectionSort from 'components/collection/sort';
import CollectionEmpty from 'components/collection/empty';
import CollectionFilter from 'components/collection/filter';
import ViewPreferencesMenu from 'components/view/preferences-menu';
import AppIcon from 'components/apps/icon';

import CollectionBulkActions from './bulk-actions';
import { CollectionRowField, fieldLinkUrl } from './table';

import './collection.scss';

export default class Collection extends React.PureComponent {
  static contextTypes = {
    client: pt.object.isRequired,
    withPreferences: pt.bool,
    fields: pt.array,
    clientFields: pt.array,
    swellFields: pt.array,
    cleanSaveField: pt.func,
  };

  static propTypes = {
    uri: pt.string,
    linkUri: pt.string,
    recordUri: pt.string,
    title: pt.string,
    model: pt.string,
    collection: pt.object,
    collectionEmpty: pt.bool,
    location: pt.object.isRequired,
    locationQuery: pt.object,
    router: pt.object.isRequired,
    bulkActions: pt.array,
    fields: pt.oneOfType([pt.array, pt.object]),
    filters: pt.oneOfType([pt.array, pt.object]),
    loading: pt.bool,
    selectable: pt.bool,
    selection: pt.object,
    disabledRecords: pt.object,
    searchable: pt.bool,
    headless: pt.bool,
    tabs: pt.array,
    client: pt.object,
    content: pt.object,
    // Extra text appended to the pagination "Showing N things" summary line.
    summaryDetail: pt.oneOfType([pt.string, pt.node]),

    onSubmitSearch: pt.func,
    onClickRow: pt.func,
    onChangeSearch: pt.func,
    onClickSelectAll: pt.func,
    onClickSelectClear: pt.func,
    onClickSelectRow: pt.func,
    onClickSort: pt.func,
    onClickPage: pt.func,
    onChangeLimit: pt.func,
    onClickBulkAction: pt.func,
  };

  constructor(props) {
    super(props);

    const enhancedFields = this.getEnhancedFields(props.fields);

    this.state = {
      activeFilters: [],
      enhancedFields,
    };

    this.searchRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const changedLocation = prevProps.location !== this.props.location;

    if (changedLocation || this.props.fields !== prevProps.fields) {
      const enhancedFields = this.getEnhancedFields(this.props.fields);

      this.setState({
        enhancedFields,
      });
    }
  }

  onSubmitSearch = (values) => {
    this.props.onSubmitSearch(values);
    this.searchRef.current?.getInputRef().select();
  };

  /** @param {React.KeyboardEvent} event */
  onKeyDownSearchEscape = (event) => {
    if (event.key === 'Escape') {
      this.props.onSubmitSearch({ search: '' });
      this.searchRef.current?.setState({ value: '' });
    }
  };

  onChangeActiveFilters = (activeFilters) => {
    this.setState({ activeFilters });
  };

  isHoverable() {
    const { collection, uri, linkUri, recordUri, onClickRow } = this.props;

    const hasLinkColumn = Boolean(uri || linkUri || recordUri || onClickRow);

    return collection?.count > 0 && hasLinkColumn;
  }

  /**
   * @param {React.MouseEvent} event
   * @param {object} record
   */
  onClickRow = async (event, record) => {
    const {
      router,
      uri,
      linkUri,
      recordUri,
      model,
      client,
      content,
      onClickRow,
    } = this.props;

    const { url } = event.currentTarget.dataset;

    const isLinkTarget = event.target.tagName === 'A';
    const editView = content?.viewsByCollection[model]?.find(
      (view) => view.id === 'edit',
    );

    let linkUrl = '';
    if (url && isLinkTarget) {
      linkUrl = url;
    } else if (typeof editView?.nav?.link === 'string') {
      linkUrl = translateAppLink(
        editView.nav,
        client.appsEnabledById[editView.app_id],
      );

      const isInternal = editView.nav.link.startsWith('frontend://');

      if (editView.nav.target === 'blank') {
        linkUrl = expandString(linkUrl, record);

        if (isInternal) {
          openFrontendAccessUrl(editView.app_id, linkUrl);
        } else {
          openWebUrl(linkUrl);
        }
        return;
      } else if (isInternal) {
        linkUrl = expandString(linkUrl, record);
      } else {
        linkUrl += `${
          linkUrl.includes('?') ? '&' : '?'
        }_external_link=${encodeURIComponent(
          expandString(editView.nav.link, record),
        )}`;
      }
    } else if (recordUri) {
      linkUrl = fieldLinkUrl(recordUri, record);
    } else if (linkUri || uri) {
      linkUrl = `${linkUri || uri}/${record.id}`;
    }

    if (!isLinkTarget) {
      if (onClickRow) {
        const result = await onClickRow(record, url);
        if (result === false) {
          return;
        }
      }

      if (linkUrl) {
        (document.getElementById('main') || window).scrollTop = 0;
        router.push(linkUrl);
      }
    }
  };

  getEnhancedFields(fields) {
    const enhancedFields = cloneDeep(objectToArray(fields));

    // Add app logos and stuff
    /* for (const field of enhancedFields) {
      const app = appsById[field.app_id];
      if (app) {
        field.label = (
          <>
            {field.label}
            <AppIcon
              image={app.logo_icon}
              name={app.name}
              size={16}
            />
          </>
        );
      }
    } */

    return enhancedFields;
  }

  onClickTab = (tab) => {
    const { location, locationQuery: lq, router } = this.props;
    const locationQuery = lq || location.query;

    // TODO: props.onQuery

    if (locationQuery.tab !== tab) {
      router.push(
        locationWithQuery(location, {
          tab: tab === 'default' ? undefined : tab,
        }),
      );
    }

    // TODO: show options when clicked while already on tab
  };

  renderTabDescription() {
    const { tabs, location, locationQuery: lq } = this.props;
    const locationQuery = lq || location.query;

    if (!locationQuery.tab) return null;
    const tab = find(tabs, { id: locationQuery.tab });

    return (
      tab && (
        <span>
          &nbsp;
          {tab.label.toLowerCase()}
        </span>
      )
    );
  }

  render() {
    const {
      title,
      collection,
      collectionEmpty,
      loading,
      location,
      locationQuery: lq,
      filters,
      selectable,
      selection = {},
      disabledRecords,
      searchable = true,
      headless = false,
      bulkActions,
      onChangeSearch,
      onClickSelectAll,
      onClickSelectClear,
      onClickSelectRow,
      onClickSort,
      onClickPage,
      onChangeLimit,
      onClickBulkAction,
      summaryDetail,
    } = this.props;

    const { activeFilters, enhancedFields } = this.state;

    const {
      client,
      withPreferences,
      fields: userFields,
      clientFields,
      swellFields,
      cleanSaveField,
    } = this.context;

    const locationQuery = lq || location.query;

    const titleLower = title?.toLowerCase();
    const visibleFields = enhancedFields.filter((field) => !field.hidden);
    const sort =
      typeof locationQuery.sort === 'string' ? locationQuery.sort : '';
    const [sortField, sortDir] = sort.split(/[\s|+]/);

    return (
      <div className="collection-container">
        {searchable && !collectionEmpty && (
          <div className="collection-search">
            <Form onSubmit={this.onSubmitSearch} onChange={onChangeSearch}>
              <Field
                ref={this.searchRef}
                name="search"
                className="collection-search-field"
                placeholder={`Search ${titleLower}`}
                defaultValue={locationQuery.search}
                value={undefined}
                onKeyDown={this.onKeyDownSearchEscape}
                autoComplete="off"
              />

              {!isEmpty(filters) && (
                <CollectionFilter
                  {...this.props}
                  filters={filters}
                  onChangeActiveFilters={this.onChangeActiveFilters}
                />
              )}
            </Form>
          </div>
        )}

        {selectable && !collectionEmpty && selection.count > 0 && (
          <ul className="collection-selection">
            <li>{selection.count} selected</li>

            <li>
              <button onClick={onClickSelectClear} type="button">
                Clear selection
              </button>
            </li>
          </ul>
        )}

        <FadeLoading active={loading} className="view-loading-mask" />

        {collection?.count > 0 ? (
          <div className="collection-table-container">
            <table
              className={classNames('collection-table outer', {
                'collection-table-hoverable': this.isHoverable(),
                headless,
              })}
            >
              {!headless && (
                <thead>
                  <tr>
                    {selectable && (
                      <th key="table-select" className="select">
                        <Checkbox
                          onClick={
                            selection.count > 0
                              ? onClickSelectClear
                              : onClickSelectAll
                          }
                          checked={
                            selection.all &&
                            Object.keys(selection.except).length === 0
                          }
                          minus={
                            (!selection.all ||
                              Object.keys(selection.except).length > 0) &&
                            selection.count > 0
                          }
                        />
                      </th>
                    )}

                    {visibleFields.reduce((list, field) => {
                      if (field.columns) {
                        field.columns.reduce((list, col, index) => {
                          list.push(
                            <th key={col.id || index} className={col.type}>
                              {field.sort !== false && col.label ? (
                                <CollectionSort
                                  field={field.id}
                                  label={col.label}
                                  dir={sortDir}
                                  sorted={field.id === sortField}
                                  onClick={onClickSort}
                                />
                              ) : (
                                col.label
                              )}
                            </th>,
                          );

                          return list;
                        }, list);
                      } else {
                        const app = client?.appsById?.[field.app_id];
                        const label = app ? (
                          <span className="collection-app-label">
                            <AppIcon
                              image={app.logo_icon}
                              name={app.name}
                              size={16}
                            />
                            {field.label}
                          </span>
                        ) : (
                          field.label
                        );

                        list.push(
                          <th
                            key={field.id}
                            className={field.template ? 'string' : field.type}
                          >
                            {field.sort !== false ? (
                              <CollectionSort
                                field={field.id}
                                label={label}
                                dir={sortDir}
                                sorted={field.id === sortField}
                                onClick={onClickSort}
                              />
                            ) : (
                              label
                            )}
                          </th>,
                        );
                      }

                      return list;
                    }, [])}

                    {withPreferences && (
                      <th key="table-action" className="action">
                        <ViewPreferencesMenu
                          sideOffset={10}
                          alignOffset={-2}
                          buttonChildren={<Icon svgType="settings-01" />}
                          preferenceKey="fields"
                          items={userFields}
                          clientItems={clientFields}
                          swellItems={swellFields}
                          cleanSaveItem={cleanSaveField}
                        />
                      </th>
                    )}
                  </tr>
                </thead>
              )}
              <tbody>
                {collection.results.length > 0 ? (
                  collection.results.map((record, index) => {
                    const isDisabled = Boolean(disabledRecords?.[record.id]);

                    return (
                      <tr
                        key={record.id || index}
                        className={classNames({
                          selected: isRecordSelected(selection, record.id),
                          disabled: isDisabled,
                          'disabled-row': isDisabled,
                        })}
                      >
                        {selectable && (
                          <td key="table-select" className="select">
                            {isDisabled ? (
                              <Checkbox
                                animated={false}
                                checked={false}
                                disabled
                              />
                            ) : (
                              <Checkbox
                                animated={false}
                                onClick={onClickSelectRow}
                                data-id={record.id}
                                checked={isRecordSelected(selection, record.id)}
                              />
                            )}
                          </td>
                        )}

                        {visibleFields.reduce((list, field) => {
                          if (field.columns) {
                            field.columns.reduce((list, col, idx) => {
                              list.push(
                                <CollectionRowField
                                  {...this.props}
                                  key={col.id || idx}
                                  id={col.id}
                                  record={record}
                                  field={col}
                                  onClick={this.onClickRow}
                                  index={index}
                                />,
                              );

                              return list;
                            }, list);
                          } else {
                            list.push(
                              <CollectionRowField
                                {...this.props}
                                key={field.id}
                                id={field.id}
                                record={record}
                                field={field}
                                onClick={this.onClickRow}
                                index={index}
                              />,
                            );
                          }

                          return list;
                        }, [])}

                        {withPreferences && (
                          <td key="table-action" className="action" />
                        )}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={visibleFields.length + (selectable ? 1 : 0)}
                      align="center"
                    >
                      <br />
                      <span className="muted">
                        There are no {titleLower} on this page.
                      </span>
                      &nbsp;
                      <button data-page="1" onClick={onClickPage} type="button">
                        Return to page 1
                      </button>
                      <br />
                      <br />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : collectionEmpty ? (
          <CollectionEmpty {...this.props} />
        ) : (
          <CollectionEmpty
            {...this.props}
            className="with-query"
            emptyTitle={false}
            emptyDescription={
              <span>
                No
                {this.renderTabDescription()}
                &nbsp;{titleLower} found
                {locationQuery.search && (
                  <span>&nbsp;matching &quot;{locationQuery.search}&quot;</span>
                )}
                {activeFilters.length > 0 && (
                  <span>&nbsp;matching filters</span>
                )}
              </span>
            }
            emptyAction={false}
          />
        )}

        {onClickPage && (
          <Pagination
            collection={collection}
            title={titleLower}
            onClickPage={onClickPage}
            onChangeLimit={onChangeLimit}
            summaryDetail={summaryDetail}
          />
        )}

        <Delayed mounted={true} mountAfter={selection.count > 0 ? 500 : 0}>
          {bulkActions && bulkActions.length > 0 && selection.count > 0 && (
            <MoveUp
              className={classNames('collection-bulk-actions', {
                visible: Boolean(selection.count),
              })}
              active={bulkActions && selection.count > 0}
              transitionAppear={true}
            >
              <span className="collection-bulk-actions-shadow" />

              <CollectionBulkActions
                actions={bulkActions}
                selection={selection}
                onClick={onClickBulkAction}
              />
            </MoveUp>
          )}
        </Delayed>
      </div>
    );
  }
}

function isRecordSelected(selection, id) {
  if (selection && selection.records) {
    if (selection.all) {
      if (selection.except[id]) {
        return false;
      }

      return true;
    }

    if (selection.records[id]) {
      return true;
    }
  }

  return false;
}
